import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { Lock, Copy, HelpCircle, ArrowRight } from 'lucide-react'
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '@/components/ui/breadcrumb'
import CopyButton from '@/components/copy-button'
import { toast } from 'sonner'

export const Route = createFileRoute('/_private/seed-list/')({
  component: RouteComponent,
})

const SUPPORT_EMAIL = 'support@inboxdoctor.ai'

function RouteComponent() {
  const navigate = useNavigate()

  const handleCopyEmailLink = async () => {
    try {
      await navigator.clipboard.writeText(`mailto:${SUPPORT_EMAIL}?subject=Seed List Access Request`)
      toast.success('Email link copied to clipboard')
    } catch (error) {
      toast.error('Failed to copy email link')
      console.error('Failed to copy email link', error)
    }
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Breadcrumbs */}
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Seed-List</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Access Restricted Content */}
        <div className="flex flex-col items-center justify-center py-12">
          {/* Lock Icon */}
          <Lock className="w-20 h-20 text-orange-500 mb-6" />
          
          {/* Access Restricted Heading */}
          <h1 className="text-4xl font-bold mb-4">Access Restricted</h1>
          
          {/* Description */}
          <p className="text-lg text-muted-foreground text-center mb-8 max-w-2xl">
            This page requires special access. Please contact our support team to request access to the seed list.
          </p>

          {/* Contact Information Card */}
          <Card className="w-full max-w-2xl mb-8 bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-900/50">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <HelpCircle className="w-5 h-5 text-orange-600 dark:text-orange-500" />
                <h2 className="text-lg font-semibold">Contact Information</h2>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium">Support Email:</span>
                <span className="text-sm text-muted-foreground">{SUPPORT_EMAIL}</span>
                <CopyButton
                  text={SUPPORT_EMAIL}
                  variant="outline"
                  size="sm"
                  buttonText="Copy Email"
                  successMessage="Email address copied to clipboard"
                />
              </div>
            </CardContent>
          </Card>

          {/* Instructions */}
          <div className="w-full max-w-2xl mb-8">
            <ol className="list-decimal list-inside space-y-2 text-left">
              <li className="text-muted-foreground">Copy the support email address above</li>
              <li className="text-muted-foreground">Send an email with subject: "Seed List Access Request"</li>
              <li className="text-muted-foreground">Include your business details and use case</li>
            </ol>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 w-full max-w-2xl">
            <Button
              onClick={handleCopyEmailLink}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Email Link
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate({ to: '/' })}
            >
              Back to Dashboard
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
